import React, { useState } from "react";

import { isPlainObject } from "lodash-es";
import { twJoin } from "tailwind-merge";
import { useDebouncedCallback } from "use-debounce";
import { validate as validateUUID } from "uuid";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { DisplayDate, DisplayDateTime } from "src/base-components/Date";
import { ExternalLink } from "src/base-components/ExternalLink";
import { FloatingWindow } from "src/base-components/FloatingWindow/FloatingWindow";
import { MarkdownRenderer } from "src/base-components/MarkdownRenderer/MarkdownRenderer";
import { CustomPopover } from "src/base-components/Popover";
import { TruncatedIdPill } from "src/base-components/TruncatedIdPill";
import {
  CellDisplayType,
  extractCurrencyAmount,
  formatCurrency,
  resolveCurrencySymbol,
  resolveEffectiveType,
  safeStringify,
} from "src/cases/caseView/blocks/cellTypes";
import { ObjectDetailPane } from "src/dataTable/ObjectDetailPane";
import { Pill } from "src/design-system/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import { copyTextToClipboard } from "src/utils/clipboard";
import { speakPythonPrimitive } from "src/utils/speakPython";
import { isProbablyMarkdown, isValidURL } from "src/utils/stringUtils";
import { useShowOnHover } from "src/utils/useShowOnHover";

export const NestedValueCell: React.FC<{
  value: unknown;
  columnKey: string;
  disableHover?: boolean;
}> = ({ value, columnKey, disableHover }) => {
  const [hovering, setHovering] = useState(false);

  const debouncedSetHovering = useDebouncedCallback(setHovering, 200);

  const handleMouseEnter = () => {
    if (disableHover) return;
    debouncedSetHovering(true);
  };

  const handleMouseLeave = () => {
    if (disableHover) return;
    debouncedSetHovering(false);
  };

  const handleFloatingWindowMouseOver = () => {
    debouncedSetHovering.cancel();
    setHovering(true);
  };

  const stringValue = safeStringify(value);

  return (
    <div
      className="min-w-0 flex-auto"
      data-loc="cell-display-nested"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <FloatingWindow
        button={
          <Pill
            fontType="code-12"
            hovered={hovering}
            size="sm"
            variant="gray"
            maxWidth
          >
            <Pill.Text>{stringValue.slice(0, 75)}</Pill.Text>
          </Pill>
        }
        draggable={true}
        isOpen={hovering}
        maximizable={true}
        offset={[0, 4]}
        pinnable={true}
        placement="auto"
        title={columnKey}
        onClose={() => setHovering(false)}
        onCopy={() => copyTextToClipboard(stringValue)}
        onMouseLeave={handleMouseLeave}
        onMouseOver={handleFloatingWindowMouseOver}
      >
        <ObjectDetailPane
          innerDimensionClass="h-full"
          jsonObject={
            Array.isArray(value)
              ? Object.fromEntries(value.map((item, i) => [String(i), item]))
              : isPlainObject(value)
                ? (value as Record<string, unknown>)
                : { value }
          }
        />
      </FloatingWindow>
    </div>
  );
};

export const TruncatedText: React.FC<{
  value: string;
  /** Tooltip content when it should differ from the copyable value. */
  tooltip?: React.ReactNode;
  children?: React.ReactNode;
  disableHover?: boolean;
  multiline?: boolean;
}> = ({ value, tooltip, children, disableHover, multiline }) => {
  const textContent = children || (
    <div
      className={twJoin(
        "min-w-0 text-gray-800",
        multiline ? "whitespace-pre-wrap break-words" : "truncate",
      )}
    >
      {value}
    </div>
  );

  if (disableHover) {
    return <>{textContent}</>;
  }

  return (
    <Tooltip
      delay={200}
      headerAction={
        <CopyTextIcon
          dataLoc="table-block-value-copy"
          feedback="inline-only-checkmark"
          size="xs"
          value={value}
        />
      }
      placement="top"
      title={tooltip ?? value}
      asChild
    >
      {textContent}
    </Tooltip>
  );
};

export const LinkValue: React.FC<{ value: string }> = ({ value }) => (
  <TruncatedText value={value}>
    <ExternalLink
      className="min-w-0 truncate hover:underline"
      href={value.startsWith("http") ? value : `https://${value}`}
    >
      {value}
    </ExternalLink>
  </TruncatedText>
);

const TagsDisplay: React.FC<{ tags: string[]; disableHover?: boolean }> = ({
  tags,
  disableHover,
}) => {
  const { isHovered, onMouseEnter, onMouseLeave } = useShowOnHover(700);

  if (tags.length === 0) {
    return null;
  }

  const [first, ...remaining] = tags;

  return (
    <div className="flex min-w-0 items-center gap-1">
      <Pill size="sm" variant="gray">
        <Pill.Text>{first}</Pill.Text>
      </Pill>
      {remaining.length > 0 &&
        (disableHover ? (
          <Pill size="sm" variant="gray">
            <Pill.Text>+{remaining.length}</Pill.Text>
          </Pill>
        ) : (
          <CustomPopover
            button={
              <span className="cursor-default">
                <Pill size="sm" variant="gray">
                  <Pill.Text>+{remaining.length}</Pill.Text>
                </Pill>
              </span>
            }
            className="flex w-70 flex-wrap justify-start gap-2 p-4"
            isOpen={isHovered}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
          >
            {remaining.map((tag) => (
              <Pill key={tag} size="sm" variant="gray">
                <Pill.Text>{tag}</Pill.Text>
              </Pill>
            ))}
          </CustomPopover>
        ))}
    </div>
  );
};

/** Wrapped string content in a fixed-height, vertically scrollable box.
 *  Shared by the editable table's display state and the read-only table so
 *  a string cell with a stored height looks the same in both. */
export const ScrollableStringCell: React.FC<{
  value: unknown;
  columnKey: string;
  displayType: CellDisplayType;
  height: number;
  disableHover?: boolean;
  className?: string;
}> = ({ value, columnKey, displayType, height, disableHover, className }) => (
  <div
    className={twJoin(
      // whitespace-normal undoes an inherited nowrap (the read-only table's
      // Cell is `truncate`) so markdown wraps the same way in both tables.
      "decideScrollbarThin min-w-0 flex-1 overflow-y-auto whitespace-normal py-1.5",
      className,
    )}
    data-loc="scrollable-string-cell"
    style={{ height }}
  >
    <TypedCellDisplay
      columnKey={columnKey}
      disableHover={disableHover}
      displayType={displayType}
      value={value}
      multiline
    />
  </div>
);

type TypedCellDisplayProps = {
  value: unknown;
  columnKey: string;
  displayType: CellDisplayType;
  disableHover?: boolean;
  multiline?: boolean;
};

export const TypedCellDisplay: React.FC<TypedCellDisplayProps> = ({
  value,
  columnKey,
  displayType,
  disableHover,
  multiline,
}) => {
  if (value === null || value === undefined) {
    return null;
  }

  const effectiveType = resolveEffectiveType(value, displayType);

  switch (effectiveType.type) {
    case "boolean":
      return (
        <Pill
          dataLoc="cell-display-boolean"
          size="sm"
          variant={value ? "green" : "red"}
        >
          <Pill.Text>{speakPythonPrimitive(String(value))}</Pill.Text>
        </Pill>
      );

    case "number":
      return (
        <div className="flex w-full justify-end" data-loc="cell-display-number">
          <TruncatedText disableHover={disableHover} value={String(value)}>
            <div className="truncate font-code-12">{String(value)}</div>
          </TruncatedText>
        </div>
      );

    case "currency": {
      const amount = extractCurrencyAmount(value);
      if (amount === null) return null;
      const formatted = formatCurrency(
        amount,
        resolveCurrencySymbol(value, effectiveType.symbol),
      );
      return (
        <div
          className="flex w-full justify-end"
          data-loc="cell-display-currency"
        >
          <TruncatedText disableHover={disableHover} value={formatted}>
            <div className="truncate font-code-12">{formatted}</div>
          </TruncatedText>
        </div>
      );
    }

    // Tooltips show the same local time as the cell; the copy action keeps the
    // raw ISO value.
    case "date":
      return typeof value === "string" ? (
        <TruncatedText
          disableHover={disableHover}
          tooltip={<DisplayDate value={value} />}
          value={value}
        >
          <div>
            <DisplayDate value={value} />
          </div>
        </TruncatedText>
      ) : (
        <TruncatedText disableHover={disableHover} value={String(value)} />
      );

    case "datetime":
      return typeof value === "string" ? (
        <TruncatedText
          disableHover={disableHover}
          tooltip={<DisplayDateTime value={value} />}
          value={value}
        >
          <div>
            <DisplayDateTime value={value} />
          </div>
        </TruncatedText>
      ) : (
        <TruncatedText disableHover={disableHover} value={String(value)} />
      );

    case "enum":
      return (
        <Pill dataLoc="cell-display-enum" size="sm" variant="gray">
          <Pill.Text>{String(value)}</Pill.Text>
        </Pill>
      );

    case "tags": {
      const tagValues = Array.isArray(value) ? value : [value];
      const tagStrings = tagValues.map((tag) =>
        typeof tag === "string" ? tag : String(tag),
      );
      return <TagsDisplay disableHover={disableHover} tags={tagStrings} />;
    }

    case "list":
    case "object":
      return (
        <NestedValueCell
          columnKey={columnKey}
          disableHover={disableHover}
          value={value}
        />
      );

    case "string": {
      const strValue = String(value);

      if (validateUUID(strValue)) {
        return (
          <TruncatedIdPill feedback="inline-only-checkmark" id={strValue} />
        );
      }

      if (isValidURL(strValue)) {
        return <LinkValue value={strValue} />;
      }

      // Markdown only: double-serialised backend responses carry literal
      // "\n". Plain strings keep it, so paths like "C:\new" survive.
      const unescaped = strValue.replace(/\\n/g, "\n");
      if (multiline && isProbablyMarkdown(unescaped)) {
        return (
          <div className="break-words text-left">
            <MarkdownRenderer content={unescaped} />
          </div>
        );
      }

      return (
        <TruncatedText
          disableHover={disableHover}
          multiline={multiline}
          value={strValue}
        />
      );
    }
  }
};
